import React, { createContext, useCallback, useState, useContext } from 'react';
import { isBefore } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';

import { singIn } from 'services/api/SignIn';
import api from 'services/api';
import { IAuthContextData, IAuthState, IUser } from './types';

const AuthContext = createContext<IAuthContextData>({} as IAuthContextData);

const AuthProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<IAuthState>(() => {
    const token = localStorage.getItem('@aguia:token');
    const tokenExpires = localStorage.getItem('@aguia:tokenExpires');
    const user = localStorage.getItem('@aguia:user');
    const permissions = localStorage.getItem('@aguia:permissions');

    if (!tokenExpires) {
      // Para evitar erros caso não tenha tokenExpires
      localStorage.removeItem('@aguia:token');
      localStorage.removeItem('@aguia:tokenExpires');
      localStorage.removeItem('@aguia:user');
      localStorage.removeItem('@aguia:permissions');

      return {} as IAuthState;
    }

    if (
      tokenExpires &&
      isBefore(
        zonedTimeToUtc(tokenExpires, 'America/Sao_Paulo'),
        zonedTimeToUtc(new Date(), 'America/Sao_Paulo'),
      )
    ) {
      localStorage.removeItem('@aguia:token');
      localStorage.removeItem('@aguia:tokenExpires');
      localStorage.removeItem('@aguia:user');
      localStorage.removeItem('@aguia:permissions');

      return {} as IAuthState;
    }

    if (token && user) {
      api.defaults.headers.authorization = `Bearer ${token}`;
      return {
        token,
        user: JSON.parse(user),
        permissions: permissions ? JSON.parse(permissions) : [],
      };
    }

    return {} as IAuthState;
  });

  const signIn = useCallback(async ({ email, password }) => {
    const response = await singIn({ email, password });

    const { token, user, expires_in, permissions } = response;

    localStorage.setItem('@aguia:token', token);
    localStorage.setItem('@aguia:tokenExpires', expires_in);
    localStorage.setItem('@aguia:user', JSON.stringify(user));
    localStorage.setItem('@aguia:permissions', JSON.stringify(permissions));

    api.defaults.headers.authorization = `Bearer ${token}`;

    setData({ token, user, permissions });
  }, []);

  const signOut = useCallback(() => {
    localStorage.removeItem('@aguia:token');
    localStorage.removeItem('@aguia:tokenExpires');
    localStorage.removeItem('@aguia:user');
    localStorage.removeItem('@aguia:permissions');

    setData({} as IAuthState);
  }, []);

  const updateUser = useCallback(
    (user: IUser) => {
      setData({
        ...data,
        token: data.token,
        user,
      });
      localStorage.setItem('@aguia:user', JSON.stringify(user));
    },
    [setData, data],
  );

  return (
    <AuthContext.Provider
      value={{
        user: data.user,
        permissions: data.permissions,
        signIn,
        signOut,
        updateUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

function useAuth(): IAuthContextData {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
}

export { AuthProvider, useAuth };
